import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';


const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState(null);

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background Image with Overlay - Same as Login */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url("/assets/images/c22a2fdf-5d74-4a81-9934-01f417fff487_121132386-1766899692964.png")'
        }}
      >
        {/* Lighter overlay for better image visibility */}
        <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" />
      </div>

      {/* Header with Glassmorphism */}
      <header className="relative bg-white/10 backdrop-blur-xl border-b border-white/20 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 text-white/90 hover:text-white transition-colors">
              <Icon name="ArrowLeft" size={20} className="drop-shadow" />
              <span className="font-heading text-lg drop-shadow-lg">Back to Home</span>
            </Link>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30 shadow-lg p-2">
                <AppImage 
                  src="/assets/images/planistry-logo-light.svg" 
                  alt="StillFlow Logo"
                  className="w-full h-full object-contain drop-shadow-lg"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={24} className="text-white drop-shadow-lg" />
                <h1 className="font-heading text-2xl font-bold text-white drop-shadow-lg">Privacy Policy</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Glassmorphism */}
      <main className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8 animate-fade-in">
          <div className="flex items-start space-x-4 mb-6">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 shadow-lg">
                <Icon name="FileText" size={24} className="text-white drop-shadow-lg" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-3xl font-bold text-white mb-4 drop-shadow-lg">StillFlow Privacy Policy</h2>
              <p className="font-body text-white/90 mb-2 drop-shadow">
                <strong>Effective Date:</strong> December 29, 2025
              </p>
              <p className="font-body text-white/90 mb-4 drop-shadow">
                <strong>Last Updated:</strong> December 29, 2025
              </p>
              <p className="font-body text-white/90 leading-relaxed drop-shadow">
                At StillFlow, we are committed to protecting your privacy and ensuring transparency about how we collect, 
                use, and safeguard your personal information. This Privacy Policy explains our data practices in compliance 
                with applicable privacy laws, including GDPR (General Data Protection Regulation) and CCPA (California Consumer Privacy Act).
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30 shadow-lg">
            <div className="flex items-start space-x-3">
              <Icon name="Mail" size={20} className="text-white mt-0.5 drop-shadow" />
              <div>
                <p className="font-body text-sm text-white/90 mb-1 drop-shadow">
                  <strong>Privacy Questions?</strong> Contact us at:
                </p>
                <a 
                  href="mailto:support@planistry.app" 
                  className="font-body text-sm text-white hover:text-white/80 font-medium underline drop-shadow"
                >
                  support@planistry.app
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="List" size={20} className="mr-2 drop-shadow" />
            Table of Contents
          </h3>
          <nav className="space-y-2">
            {[
              { id: 'data-collection', title: '1. Information We Collect' },
              { id: 'data-usage', title: '2. How We Use Your Information' },
              { id: 'third-party', title: '3. Third-Party Services' },
              { id: 'data-sharing', title: '4. Data Sharing and Disclosure' },
              { id: 'user-rights', title: '5. Your Rights and Choices' },
              { id: 'data-security', title: '6. Data Security' },
              { id: 'data-retention', title: '7. Data Retention' },
              { id: 'international', title: '8. International Data Transfers' },
              { id: 'cookies', title: '9. Cookies and Tracking' },
              { id: 'children', title: '10. Children\'s Privacy' },
              { id: 'gdpr-rights', title: '11. GDPR Rights (European Users)' },
              { id: 'ccpa-rights', title: '12. CCPA Rights (California Users)' },
              { id: 'changes', title: '13. Changes to This Policy' }
            ]?.map(({ id, title }) => (
              <a
                key={id}
                href={`#${id}`}
                className="block font-body text-white/90 hover:text-white hover:bg-white/20 px-4 py-2 rounded-lg transition-all duration-300 backdrop-blur-sm drop-shadow"
              >
                {title}
              </a>
            ))}
          </nav>
        </div>

        {/* Section 1: Information We Collect */}
        <div id="data-collection" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="Database" size={24} className="mr-2 drop-shadow" />
            1. Information We Collect
          </h3>
          
          <div className="space-y-6">
            <div>
              <h4 className="font-heading text-lg font-semibold text-white mb-3 drop-shadow">Account Information</h4>
              <p className="font-body text-white/90 mb-2 drop-shadow">When you create an account, we collect:</p>
              <ul className="list-disc list-inside font-body text-white/90 space-y-1 ml-4 drop-shadow">
                <li>Full name</li>
                <li>Email address</li>
                <li>Password (encrypted and securely stored)</li>
                <li>Newsletter subscription preference (optional)</li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading text-lg font-semibold text-white mb-3 drop-shadow">Planner Data</h4>
              <p className="font-body text-white/90 mb-2 drop-shadow">We store your planner-related information, including:</p>
              <ul className="list-disc list-inside font-body text-white/90 space-y-1 ml-4 drop-shadow">
                <li>Planner configurations and customizations</li>
                <li>Custom content you create within your planners</li>
                <li>Generated PDF files (stored temporarily for download)</li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading text-lg font-semibold text-white mb-3 drop-shadow">Usage Analytics (Planned)</h4>
              <p className="font-body text-white/90 mb-2 drop-shadow">In the future, we will collect:</p>
              <ul className="list-disc list-inside font-body text-white/90 space-y-1 ml-4 drop-shadow">
                <li>Page views and feature usage patterns</li>
                <li>User behavior analytics for service improvement</li>
                <li>Error logs and technical diagnostics</li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading text-lg font-semibold text-white mb-3 drop-shadow">Payment Information</h4>
              <p className="font-body text-white/90 drop-shadow">
                Payment transactions are processed by <strong>Stripe</strong>. We do not store your full payment card details. 
                We only retain:
              </p>
              <ul className="list-disc list-inside font-body text-white/90 space-y-1 ml-4 mt-2 drop-shadow">
                <li>Payment confirmation status</li>
                <li>Subscription length and type (if applicable)</li>
                <li>Transaction IDs for record-keeping</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 2: How We Use Your Information */}
        <div id="data-usage" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="Target" size={24} className="mr-2 drop-shadow" />
            2. How We Use Your Information
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-white mt-0.5 drop-shadow" />
              <div>
                <h4 className="font-heading text-lg font-semibold text-white mb-1 drop-shadow">Service Delivery</h4>
                <p className="font-body text-white/90 drop-shadow">
                  Provide, maintain, and improve our planner creation and customization services
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-white mt-0.5 drop-shadow" />
              <div>
                <h4 className="font-heading text-lg font-semibold text-white mb-1 drop-shadow">Communication</h4>
                <p className="font-body text-white/90 drop-shadow">
                  Send service-related updates, newsletters (if opted in), and respond to support requests
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-white mt-0.5 drop-shadow" />
              <div>
                <h4 className="font-heading text-lg font-semibold text-white mb-1 drop-shadow">Service Improvement</h4>
                <p className="font-body text-white/90 drop-shadow">
                  Analyze usage patterns to enhance features, fix bugs, and optimize user experience
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-white mt-0.5 drop-shadow" />
              <div>
                <h4 className="font-heading text-lg font-semibold text-white mb-1 drop-shadow">Customer Support</h4>
                <p className="font-body text-white/90 drop-shadow">
                  Process and respond to your inquiries, technical issues, and support requests
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-white mt-0.5 drop-shadow" />
              <div>
                <h4 className="font-heading text-lg font-semibold text-white mb-1 drop-shadow">Legal Compliance</h4>
                <p className="font-body text-white/90 drop-shadow">
                  Comply with legal obligations, prevent fraud, and protect our rights
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Third-Party Services */}
        <div id="third-party" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="Link" size={24} className="mr-2 drop-shadow" />
            3. Third-Party Services
          </h3>
          
          <div className="space-y-6">
            <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Supabase</h4>
              <p className="font-body text-white/90 mb-2 drop-shadow">
                <strong>Purpose:</strong> Authentication, database storage, and file storage
              </p>
              <p className="font-body text-white/90 mb-2 drop-shadow">
                <strong>Data Stored:</strong> Account credentials, planner configurations, user-generated content
              </p>
              <p className="font-body text-white/90 drop-shadow">
                <strong>Location:</strong> Servers distributed globally
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Stripe</h4>
              <p className="font-body text-white/90 mb-2 drop-shadow">
                <strong>Purpose:</strong> Payment processing and subscription management
              </p>
              <p className="font-body text-white/90 mb-2 drop-shadow">
                <strong>Data Processed:</strong> Payment card information, billing details
              </p>
              <p className="font-body text-white/90 drop-shadow">
                <strong>Note:</strong> StillFlow does not store your full payment information
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Google Analytics (Planned)</h4>
              <p className="font-body text-white/90 mb-2 drop-shadow">
                <strong>Purpose:</strong> Website analytics and user behavior tracking
              </p>
              <p className="font-body text-white/90 drop-shadow">
                <strong>Data Collected:</strong> Page views, session duration, demographic information
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Advertising Networks (Planned)</h4>
              <p className="font-body text-white/90 mb-2 drop-shadow">
                <strong>Purpose:</strong> Display relevant advertisements
              </p>
              <p className="font-body text-white/90 drop-shadow">
                <strong>Data Shared:</strong> Anonymized usage data for targeted advertising
              </p>
            </div>
          </div>
        </div>

        {/* Section 4: Data Sharing and Disclosure */}
        <div id="data-sharing" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="Share2" size={24} className="mr-2 drop-shadow" />
            4. Data Sharing and Disclosure
          </h3>
          
          <div className="space-y-4">
            <div className="bg-yellow-500/20 backdrop-blur-sm border border-yellow-400/30 rounded-lg p-4">
              <p className="font-body text-white mb-2 drop-shadow">
                <strong>Current Practice:</strong> We do not currently share your personal data with third-party partners.
              </p>
              <p className="font-body text-white/90 drop-shadow">
                <strong>Future Plans:</strong> We reserve the right to share anonymized or aggregated data for marketing 
                purposes. If we implement data sharing, you will have the option to opt out.
              </p>
            </div>

            <div>
              <h4 className="font-heading text-lg font-semibold text-white mb-3 drop-shadow">We May Disclose Information:</h4>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={18} className="text-white mt-0.5 drop-shadow" />
                  <span className="font-body text-white/90 drop-shadow">To comply with legal obligations or court orders</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={18} className="text-white mt-0.5 drop-shadow" />
                  <span className="font-body text-white/90 drop-shadow">To protect our rights, property, or safety</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={18} className="text-white mt-0.5 drop-shadow" />
                  <span className="font-body text-white/90 drop-shadow">In connection with a business merger or acquisition</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={18} className="text-white mt-0.5 drop-shadow" />
                  <span className="font-body text-white/90 drop-shadow">With service providers who assist in operations (under strict confidentiality)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 5: Your Rights and Choices */}
        <div id="user-rights" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="User" size={24} className="mr-2 drop-shadow" />
            5. Your Rights and Choices
          </h3>
          
          <div className="space-y-4">
            <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Account Deletion</h4>
              <p className="font-body text-white/90 drop-shadow">
                You can delete your account at any time. Upon deletion, all associated data will be removed within 30 days.
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Data Export</h4>
              <p className="font-body text-white/90 drop-shadow">
                You can download your planner configurations and custom content at any time through your account dashboard.
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Communication Preferences</h4>
              <p className="font-body text-white/90 drop-shadow">
                You can opt out of marketing emails and newsletters at any time through your account settings or by clicking 
                the unsubscribe link in any email.
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Marketing Opt-Out</h4>
              <p className="font-body text-white/90 drop-shadow">
                If we implement marketing data sharing, you will have the right to opt out of having your data used for 
                marketing purposes.
              </p>
            </div>
          </div>
        </div>

        {/* Section 6: Data Security */}
        <div id="data-security" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="Lock" size={24} className="mr-2 drop-shadow" />
            6. Data Security
          </h3>
          
          <div className="space-y-4">
            <p className="font-body text-white/90 drop-shadow">
              We implement industry-standard security measures to protect your personal information:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Shield" size={20} className="text-white drop-shadow" />
                  <h4 className="font-heading text-base font-semibold text-white drop-shadow">SSL Encryption</h4>
                </div>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  All data transmitted between your browser and our servers is encrypted using SSL/TLS protocols
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Key" size={20} className="text-white drop-shadow" />
                  <h4 className="font-heading text-base font-semibold text-white drop-shadow">Password Encryption</h4>
                </div>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Passwords are hashed and encrypted using industry-standard algorithms
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Database" size={20} className="text-white drop-shadow" />
                  <h4 className="font-heading text-base font-semibold text-white drop-shadow">Secure Storage</h4>
                </div>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Data is stored on secure servers with access controls and regular backups
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="AlertTriangle" size={20} className="text-white drop-shadow" />
                  <h4 className="font-heading text-base font-semibold text-white drop-shadow">Breach Notification</h4>
                </div>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  We will notify affected users promptly in the event of a data breach
                </p>
              </div>
            </div>

            <div className="bg-yellow-500/20 backdrop-blur-sm border border-yellow-400/30 rounded-lg p-4 mt-4">
              <p className="font-body text-sm text-white/90 drop-shadow">
                <strong>Note:</strong> While we implement strong security measures, no method of transmission over 
                the internet is 100% secure. We cannot guarantee absolute security of your data.
              </p>
            </div>
          </div>
        </div>

        {/* Section 7: Data Retention */}
        <div id="data-retention" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="Clock" size={24} className="mr-2 drop-shadow" />
            7. Data Retention
          </h3>
          
          <div className="space-y-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Active Accounts</h4>
              <p className="font-body text-white/90 drop-shadow">
                We retain your data for as long as your account remains active and you continue using our services.
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">After Account Deletion</h4>
              <p className="font-body text-white/90 mb-2 drop-shadow">
                When you delete your account, we will:
              </p>
              <ul className="list-disc list-inside font-body text-white/90 space-y-1 ml-4 drop-shadow">
                <li>Retain your data for <strong>30 days</strong> to allow for account recovery</li>
                <li>Permanently destroy all personal data after the 30-day period using Supabase deletion practices</li>
                <li>Delete database records to ensure complete removal</li>
              </ul>
            </div>

            <div className="bg-yellow-500/20 backdrop-blur-sm border border-yellow-400/30 rounded-lg p-4">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Legal Retention</h4>
              <p className="font-body text-white/90 drop-shadow">
                Some data may be retained longer than 30 days for legal, compliance, or security purposes, including:
              </p>
              <ul className="list-disc list-inside font-body text-white/90 space-y-1 ml-4 mt-2 drop-shadow">
                <li>Transaction records for accounting and tax purposes</li>
                <li>Legal correspondence and dispute records</li>
                <li>Security logs to prevent fraud and abuse</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 8: International Data Transfers */}
        <div id="international" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="Globe" size={24} className="mr-2 drop-shadow" />
            8. International Data Transfers
          </h3>
          
          <div className="space-y-4">
            <p className="font-body text-white/90 drop-shadow">
              StillFlow is based in the <strong>United States</strong> and serves users internationally. Your data may 
              be transferred to and processed in countries outside your country of residence.
            </p>

            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Supabase Global Infrastructure</h4>
              <p className="font-body text-white/90 drop-shadow">
                Our database provider, Supabase, operates servers distributed globally. Your data may be stored and 
                processed in multiple geographic regions to ensure service reliability and performance.
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Data Protection Standards</h4>
              <p className="font-body text-white/90 drop-shadow">
                When transferring data internationally, we ensure appropriate safeguards are in place to protect your 
                information in accordance with applicable privacy laws, including:
              </p>
              <ul className="list-disc list-inside font-body text-white/90 space-y-1 ml-4 mt-2 drop-shadow">
                <li>Standard contractual clauses approved by regulatory authorities</li>
                <li>Compliance with cross-border data transfer regulations</li>
                <li>Encryption of data in transit and at rest</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 9: Cookies and Tracking */}
        <div id="cookies" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="Cookie" size={24} className="mr-2 drop-shadow" />
            9. Cookies and Tracking Technologies
          </h3>
          
          <div className="space-y-4">
            <p className="font-body text-white/90 drop-shadow">
              We use cookies and similar tracking technologies to enhance your experience and collect usage data.
            </p>

            <div className="space-y-3">
              <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
                <h4 className="font-heading text-base font-semibold text-white mb-1 drop-shadow">Essential Cookies</h4>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Required for basic functionality, authentication, and security. You cannot opt out of these.
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
                <h4 className="font-heading text-base font-semibold text-white mb-1 drop-shadow">Analytics Cookies (Planned)</h4>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Help us understand how users interact with our service through Google Analytics. You can opt out through 
                  browser settings or cookie preferences.
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
                <h4 className="font-heading text-base font-semibold text-white mb-1 drop-shadow">Advertising Cookies (Planned)</h4>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Used to display relevant advertisements based on your interests. You can manage these through your 
                  cookie preferences.
                </p>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
              <h4 className="font-heading text-base font-semibold text-white mb-2 drop-shadow">Managing Cookies</h4>
              <p className="font-body text-sm text-white/90 mb-2 drop-shadow">You can control cookies through:</p>
              <ul className="list-disc list-inside font-body text-sm text-white/90 space-y-1 ml-4 drop-shadow">
                <li>Browser settings (most browsers allow you to refuse or delete cookies)</li>
                <li>Cookie preference center (coming soon)</li>
                <li>Opt-out tools provided by advertising networks</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 10: Children's Privacy */}
        <div id="children" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="Users" size={24} className="mr-2 drop-shadow" />
            10. Children's Privacy
          </h3>
          
          <div className="space-y-4">
            <div className="bg-red-500/20 backdrop-blur-sm border border-red-400/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={24} className="text-white mt-0.5 drop-shadow" />
                <div>
                  <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Age Requirement: 16+</h4>
                  <p className="font-body text-white/90 mb-2 drop-shadow">
                    StillFlow is not intended for users under the age of <strong>16</strong>. We do not knowingly collect 
                    personal information from children under 16 without parental consent.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Age Verification</h4>
              <p className="font-body text-white/90 mb-2 drop-shadow">
                During account registration, users must confirm they are at least 16 years old by checking an age 
                verification box.
              </p>
              <p className="font-body text-white/90 drop-shadow">
                <strong>Future Plans:</strong> We plan to implement additional age verification measures to protect 
                users under 18 from using the service without parental approval.
              </p>
            </div>

            <div className="bg-yellow-500/20 backdrop-blur-sm border border-yellow-400/30 rounded-lg p-4">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">If You Are Under 16</h4>
              <p className="font-body text-white/90 drop-shadow">
                If you are under 16, please do not use StillFlow or provide any personal information. If we discover 
                that we have collected information from a user under 16, we will delete that information immediately.
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
              <h4 className="font-heading text-lg font-semibold text-white mb-2 drop-shadow">Parental Rights</h4>
              <p className="font-body text-white/90 drop-shadow">
                If you believe your child under 16 has provided information to us, please contact us at 
                <a href="mailto:support@planistry.app" className="text-white hover:text-white/80 font-medium ml-1">
                  support@planistry.app
                </a>
                , and we will take steps to delete the information.
              </p>
            </div>
          </div>
        </div>

        {/* Section 11: GDPR Rights (European Users) */}
        <div id="gdpr-rights" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="Shield" size={24} className="mr-2 drop-shadow" />
            11. GDPR Rights (European Users)
          </h3>
          
          <div className="space-y-4">
            <p className="font-body text-white/90 drop-shadow">
              If you are located in the European Economic Area (EEA), you have specific rights under the General Data 
              Protection Regulation (GDPR):
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Eye" size={20} className="text-white drop-shadow" />
                  <h4 className="font-heading text-base font-semibold text-white drop-shadow">Right to Access</h4>
                </div>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Request a copy of all personal data we hold about you
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Edit" size={20} className="text-white drop-shadow" />
                  <h4 className="font-heading text-base font-semibold text-white drop-shadow">Right to Rectification</h4>
                </div>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Correct inaccurate or incomplete personal data
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Trash2" size={20} className="text-white drop-shadow" />
                  <h4 className="font-heading text-base font-semibold text-white drop-shadow">Right to Erasure</h4>
                </div>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Request deletion of your personal data ("right to be forgotten")
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Pause" size={20} className="text-white drop-shadow" />
                  <h4 className="font-heading text-base font-semibold text-white drop-shadow">Right to Restriction</h4>
                </div>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Limit how we use your personal data in certain circumstances
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Download" size={20} className="text-white drop-shadow" />
                  <h4 className="font-heading text-base font-semibold text-white drop-shadow">Right to Data Portability</h4>
                </div>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Receive your data in a structured, machine-readable format
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="XCircle" size={20} className="text-white drop-shadow" />
                  <h4 className="font-heading text-base font-semibold text-white drop-shadow">Right to Object</h4>
                </div>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Object to processing of your data for certain purposes
                </p>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
              <h4 className="font-heading text-base font-semibold text-white mb-2 drop-shadow">How to Exercise Your Rights</h4>
              <p className="font-body text-sm text-white/90 drop-shadow">
                To exercise any of these rights, please contact us at 
                <a href="mailto:support@planistry.app" className="text-white hover:text-white/80 font-medium ml-1">
                  support@planistry.app
                </a>
                . We will respond to your request within <strong>30 days</strong>.
              </p>
            </div>
          </div>
        </div>

        {/* Section 12: CCPA Rights (California Users) */}
        <div id="ccpa-rights" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="MapPin" size={24} className="mr-2 drop-shadow" />
            12. CCPA Rights (California Users)
          </h3>
          
          <div className="space-y-4">
            <p className="font-body text-white/90 drop-shadow">
              If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):
            </p>

            <div className="space-y-3">
              <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
                <h4 className="font-heading text-base font-semibold text-white mb-1 drop-shadow">Right to Know</h4>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Request disclosure of the categories and specific pieces of personal information we collect
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
                <h4 className="font-heading text-base font-semibold text-white mb-1 drop-shadow">Right to Delete</h4>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Request deletion of your personal information (subject to certain exceptions)
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
                <h4 className="font-heading text-base font-semibold text-white mb-1 drop-shadow">Right to Opt-Out</h4>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  Opt out of the "sale" of personal information (we do not currently sell personal information)
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm border-l-4 border-white/50 pl-4 py-2 rounded">
                <h4 className="font-heading text-base font-semibold text-white mb-1 drop-shadow">Right to Non-Discrimination</h4>
                <p className="font-body text-sm text-white/90 drop-shadow">
                  You will not receive discriminatory treatment for exercising your CCPA rights
                </p>
              </div>
            </div>

            <div className="bg-yellow-500/20 backdrop-blur-sm border border-yellow-400/30 rounded-lg p-4">
              <h4 className="font-heading text-base font-semibold text-white mb-2 drop-shadow">Do Not Sell My Personal Information</h4>
              <p className="font-body text-sm text-white/90 mb-2 drop-shadow">
                <strong>Current Practice:</strong> We do not sell your personal information to third parties.
              </p>
              <p className="font-body text-sm text-white/90 drop-shadow">
                <strong>Future Plans:</strong> If we implement data sharing for marketing purposes, you will have the 
                right to opt out of such sharing.
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
              <h4 className="font-heading text-base font-semibold text-white mb-2 drop-shadow">How to Submit a Request</h4>
              <p className="font-body text-sm text-white/90 drop-shadow">
                California residents can submit requests by emailing 
                <a href="mailto:support@planistry.app" className="text-white hover:text-white/80 font-medium ml-1">
                  support@planistry.app
                </a>
                . We will verify your identity and respond within <strong>45 days</strong>.
              </p>
            </div>
          </div>
        </div>

        {/* Section 13: Changes to This Policy */}
        <div id="changes" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 mb-8">
          <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center drop-shadow-lg">
            <Icon name="RefreshCw" size={24} className="mr-2 drop-shadow" />
            13. Changes to This Privacy Policy
          </h3>
          
          <div className="space-y-4">
            <p className="font-body text-white/90 drop-shadow">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, 
              legal requirements, or other factors.
            </p>

            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
              <h4 className="font-heading text-base font-semibold text-white mb-2 drop-shadow">Notification of Changes</h4>
              <p className="font-body text-sm text-white/90 drop-shadow">
                When we make material changes to this policy, we will:
              </p>
              <ul className="list-disc list-inside font-body text-sm text-white/90 space-y-1 ml-4 mt-2 drop-shadow">
                <li>Update the "Last Updated" date at the top of this page</li>
                <li>Send an email notification to registered users (if changes are significant)</li>
                <li>Display a prominent notice on our website</li>
              </ul>
            </div>

            <div className="bg-yellow-500/20 backdrop-blur-sm border border-yellow-400/30 rounded-lg p-4">
              <p className="font-body text-sm text-white/90 drop-shadow">
                <strong>Your Responsibility:</strong> We encourage you to review this Privacy Policy periodically. 
                Continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-indigo-600/40 to-purple-600/40 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 text-white">
          <div className="text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/30">
              <Icon name="Mail" size={32} className="text-white drop-shadow-lg" />
            </div>
            <h3 className="font-heading text-2xl font-bold mb-2 drop-shadow-lg">Questions or Concerns?</h3>
            <p className="font-body text-white/90 mb-4 drop-shadow">
              If you have any questions about this Privacy Policy or how we handle your data, 
              please don't hesitate to contact us.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
              <a 
                href="mailto:support@planistry.app"
                className="inline-flex items-center space-x-2 bg-white text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-white/90 transition-all duration-300 hover:scale-[1.02] shadow-lg"
              >
                <Icon name="Mail" size={20} />
                <span>support@planistry.app</span>
              </a>
              <Link
                to="/user-account-dashboard"
                className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/30 transition-all duration-300 border border-white/30 shadow-lg hover:scale-[1.02]"
              >
                <Icon name="Settings" size={20} />
                <span>Account Settings</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="font-caption text-sm text-white/90 drop-shadow">
            This privacy policy is effective as of December 29, 2025 and applies to all users of StillFlow.
          </p>
        </div>
      </main>

      {/* Footer with Glassmorphism */}
      <footer className="relative bg-white/10 backdrop-blur-xl border-t border-white/20 py-6 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
            <p className="font-body text-sm text-white/90 drop-shadow">
              © 2025 StillFlow. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <Link to="/" className="font-body text-sm text-white/90 hover:text-white transition-colors drop-shadow">
                Home
              </Link>
              <button className="font-body text-sm text-white/90 hover:text-white transition-colors drop-shadow">
                Terms of Service
              </button>
              <Link to="/privacy-policy" className="font-body text-sm text-white font-medium drop-shadow-lg">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;